import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import CardsIcon from '@material-ui/icons/ViewModule';
import TableIcon from '@material-ui/icons/List';
import KeyIcon from '@material-ui/icons/RadioButtonUnchecked';
import SearchBar from 'tuc-webui-base/components/SearchBar';
import { extendViewService } from 'tuc-webui-base/services/ViewService';
import { useRowNumber, usePendingSelection } from 'tuc-webui-base/components/TableViewComponent';
import ConfirmDialog from 'tuc-webui-base/components/ConfirmDialog';
import MessageDialog from 'grb-webui/components/MessageDialog';
import FilterBar, { withPageFilter } from 'grb-webui/components/FilterBar';
import { ActionButtons, useMenu } from 'grb-webui/components/Actions';
import { TABLE_PANEL_TYPE } from 'grb-webui/ComponentTypes';
import { StateEditor } from 'grb-webui/editors';
import { withStoreData } from 'grb-webui/store';
import {
  API_KEYS_ENTITY,
} from '../../Entities';
import {
  API_KEY_CARD_TYPE,
  API_KEY_CARDS_TYPE,
} from '../../ComponentTypes';
import AppMainPage, { FIT_TO_PAGE_VARIANT } from '../../components/AppMainPage';
import APIKeyCardHOC from './APIKeyCard';
import APIKeyCards from './APIKeyCards';
import KeyFilter from './KeyFilter';
import APIKeyActions from './APIKeyActions';
import CreateKeyDialog from './CreateKeyDialog';
import './APIKeysContainer.scss';

const CARDS_MODE = 'cards';
const TABLE_MODE = 'table';

export default (options = {}) => {
  const actions = options.actions ? { ...APIKeyActions(options), ...options.actions } : APIKeyActions(options);
  const { useDisable } = options;
  const {
    rowButtons = ['edit', 'menu'],
    rowMenuItems = (useDisable ? ['disable'] : []).concat('delete'),
    licensesEntity = 'licenses',
    validateCreate,
  } = options;
  const actionOptions = { getId: options.getKey };

  const getExtraFilterValues = k => (k.disabled ? ['disabled'] : []);

  const ViewServiceProperties = {
    componentsByName: {
      APIKeyRowButtons: ActionButtons(actions, rowButtons, actionOptions),
    },
    editors: [
      new StateEditor('keyState', {
        states: [
          {
            values: [false, undefined],
            icon: KeyIcon,
            text: 'Enabled',
            className: 'grb-key-icon grb-key-icon-enabled',
            tooltip: 'API Key Enabled',
          },
          {
            value: true,
            icon: KeyIcon,
            text: 'Disabled',
            className: 'grb-key-icon grb-key-icon-disabled',
            tooltip: 'API Key Disabled',
          },
        ],
        checkedOn: true,
      }),
    ],
  };

  const makeDialog = (opt = {}, defaultDialog) => {
    const { dialog } = opt;
    if (dialog) {
      if (dialog.create) {
        const { create, ...params } = dialog;
        return [dialog.create(options), params];
      }
      if (typeof dialog === 'function') {
        return [dialog(options)];
      }
    }
    return defaultDialog ? [defaultDialog(options), dialog] : [];
  };

  const [CreateAPIKeyDialog, createDialogProps = {}] = makeDialog(options.create, CreateKeyDialog);
  const [EditAPIKeyDialog, editDialogProps = {}] = makeDialog(options.edit, CreateKeyDialog);
  const [NewCreatedAPIKeyDialog] = makeDialog(options.created);
  const { backTo } = options;

  const APIKeysContainer = (props) => {
    const {
      filter,
      arrayFilter,
      onFilterChange,
      filterEditor,
      onUpdateAPIKeys,
      search,
      onSearchChange,
      [licensesEntity]: licenses,
      configuration,
      ...rest
    } = props;
    const {
      viewService,
      apiKeyService,
      onSnackbarMessage,
    } = props;
    const apiKeys = props[options.keysEntity || API_KEYS_ENTITY];
    const [tableProps] = useState(() => (options.tableProps || {}));
    const [messageDialogOpen, setMessageDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState({});
    const [confirmDialog, setConfirmDialog] = useState({ open: false });
    const [CardsPanel] = useState(() => (
      options.cards && (viewService.getComponent(API_KEY_CARDS_TYPE) || APIKeyCards(options.cardsConfig))));
    const [TablePanel] = useState(() => viewService.getComponent(TABLE_PANEL_TYPE));
    const [APIKeyCard] = useState(() => (
      options.cards
        ? (viewService.getComponent(API_KEY_CARD_TYPE) || APIKeyCardHOC({
            ...options, actions, rowButtons, actionOptions }))
        : undefined));
    const apiKeysCount = useRowNumber(apiKeys, false, options.serverFiltering);
    const [extViewService] = useState(() => extendViewService(viewService, ViewServiceProperties));
    const [newCreatedAPIKey, setNewCreatedAPIKey] = useState(null);
    const [newGeneratedSecretAPIKey, setNewGeneratedSecretAPIKey] = useState(null);
    const [createKeyDialog, setCreateKeyDialog] = useState({});
    const [editKeyDialog, setEditKeyDialog] = useState({ open: false });
    const getKeyId = useCallback((key) => {
      return options.getKey ? options.getKey(key) : key.key;
    }, [options]);
    const getKey = options.getKey ? options.getKey : k => k.key;
    const { menuProps, onOpenMenu, Menu } = useMenu(actions, rowMenuItems, actionOptions);
    const { selected, setPendingSelectionId, onSelect } = usePendingSelection(null, apiKeys, getKey);

    const getFilterValues = useCallback((k) => {
      return [
        getKey(k),
        k.licenseId,
        k.user && k.user.email,
        k.usedIP,
        k.description,
        k.disabled && 'disabled',
      ].filter(o => o);
    }, []);

    const mode = filter.mode || (!options.cards ? TABLE_MODE : CARDS_MODE);
    // Update route when changing display mode
    const handleMode = (event, newMode) => {
      if (newMode && (newMode !== mode)) {
        onFilterChange(Object.assign({}, filter, { mode: newMode }));
      }
    };

    //
    // Delete key
    //
    const handleDeleteKey = useCallback((key) => {
      setConfirmDialog({ open: false });
      if (key) {
        apiKeyService.deleteApiKey(key)
        .then(() => {
          onSnackbarMessage({ message: 'API Key deleted' });
        })
        .catch(e => viewService.onError(e));
      }
    }, [onSnackbarMessage, apiKeyService]);

    const handlePromptDeleteKey = useCallback((key) => {
      const msg = apiKeyService.validateDeleteApiKey(key)
        || (options.cannotDeleteLast && key.licenseId
              && (apiKeys.filter(k => k.licenseId === key.licenseId).length === 1)
              && `Cannot delete the last API key associated with the license ${key.licenseId}`);
      if (msg) {
        setDialogMessage({ title: 'Delete key error', message: msg });
        setMessageDialogOpen(true);
      } else {
        setConfirmDialog({
          open: true,
          title: 'Are you sure you want to delete this API key?',
          message: 'Existing applications must be updated with a new key.',
          buttons: [
            { title: 'Cancel', value: null, id: 'cancel-api-key-delete' },
            { title: 'Delete API key', value: key, id: 'ok-api-key-delete' }],
          onClose: handleDeleteKey,
        });
      }
    }, [apiKeys]);

    const handleCloseMessageDialog = () => {
      setMessageDialogOpen(false);
    };

    //
    // Enable/disable key
    //
    const handleDisableKey = useCallback(apiKeyService.disableApiKey ? (key) => {
      const disableKey = (k) => {
        setConfirmDialog({ open: false });
        if (k) {
          const enable = k.disabled;
          apiKeyService.disableApiKey(k, enable)
          .then(() => {
            onSnackbarMessage({ message: `API Key ${enable ? 'enabled' : 'disabled'}` });
          })
          .catch(e => viewService.onError(e));
        }
      };

      const msg = apiKeyService.validateDisableKey(key);
      if (msg) {
        setDialogMessage({ title: 'Disable key error', message: msg });
        setMessageDialogOpen(true);
      } else if (!key.disabled) {
        setConfirmDialog({
          open: true,
          title: 'Are you sure you want to disable this API key?',
          message: 'Existing applications using this key won\'t get authorized anymore.',
          buttons: [{ title: 'Cancel', value: null }, { title: 'Confirm disabling this API key', value: key }],
          onClose: disableKey,
        });
      } else {
        disableKey(key);
      }
    } : undefined, [onSnackbarMessage, apiKeyService, viewService]);

    //
    // Regenerate secret key
    //
    const handleGenerateSecret = useCallback((key) => {
      setConfirmDialog({ open: false });
      if (key) {
        apiKeyService.generateApiKeySecret(key)
        .then((newKey) => {
          setNewGeneratedSecretAPIKey({
            key: options.getKey ? options.getKey(key) : key,
            secret: options.getSecret ? options.getSecret(newKey) : newKey.secret,
          });
        })
        .catch(e => viewService.onError(e));
      }
    }, [onSnackbarMessage, apiKeyService]);

    const handlePromptGenerateSecretKey = useCallback((key) => {
      const msg = apiKeyService.validateGenerateApiKeySecret(key);
      if (msg) {
        setDialogMessage({ title: 'Generate secret error', message: msg });
        setMessageDialogOpen(true);
      } else {
        setConfirmDialog({
          open: true,
          title: 'Are you sure you want to delete this API key?',
          message: 'Existing applications must be updated with a new key.',
          buttons: [{ title: 'Cancel', value: null }, { title: 'Confirm generation of secret', value: key }],
          onClose: handleGenerateSecret,
        });
      }
    }, [handleGenerateSecret]);

    //
    // Edit API key
    //
  const handleEditKey = useCallback((key, params) => {
      setEditKeyDialog({ open: false });
      if (params !== undefined) {
        apiKeyService.editApiKey(key, params)
        .then(() => {
          onSnackbarMessage({ message: 'API Key edited' });
        })
        .catch(e => viewService.onError(e));
      }
    }, [onSnackbarMessage, apiKeyService]);

    const handlePromptEditKey = useCallback(apiKeyService.editApiKey && EditAPIKeyDialog ? (key) => {
      const msg = apiKeyService.validateEditApiKey(key);
      if (msg) {
        setDialogMessage({ title: 'Edit key error', message: msg });
        setMessageDialogOpen(true);
      } else {
        setEditKeyDialog({ open: true, key });
      }
    } : undefined, [onSnackbarMessage, apiKeyService]);

    //
    // Create API key
    //
    const handleCreateKey = useCallback((params) => {
      setCreateKeyDialog({ open: false, creating: params !== undefined });
      if (params !== undefined) {
        apiKeyService.createApiKey(params)
        .then((newKey) => {
          const k = getKey(newKey);
          setPendingSelectionId(k);
          setNewCreatedAPIKey({
            ...newKey,
            key: k,
            secret: options.getSecret ? options.getSecret(newKey) : newKey.secret,
          });
        })
        .catch(e => viewService.onError(e))
        .finally(() => {
          setCreateKeyDialog({ creating: false });
        });
      }
    }, [onSnackbarMessage, apiKeyService]);

    const handlePromptCreateKey = useCallback(() => {
      const msg = validateCreate && validateCreate() || apiKeyService.validateCreateApiKey();
      if (msg) {
        setDialogMessage({ title: 'Create key error', message: msg });
        setMessageDialogOpen(true);
      } else if (CreateAPIKeyDialog) {
        setCreateKeyDialog({ open: true });
      } else {
        handleCreateKey({});
      }
    }, [onSnackbarMessage, apiKeyService]);

    //
    // Key and secret creations
    //
    const handleCloseAPIKeyDialog = () => {
      setNewCreatedAPIKey(null);
      setNewGeneratedSecretAPIKey(null);
    };

    const filteredKeys = arrayFilter ? arrayFilter(apiKeys) : apiKeys;

    //
    // Header
    //
    const HeaderBar = (
      <React.Fragment>
        {(options.noFilter !== true) && (
          <FilterBar
            {...rest}
            filter={filter || {}}
            filterEditor={filterEditor}
            onFilterChange={onFilterChange}
          />
        )}
        <SearchBar
          {...props}
          placeholder={mode === CARDS_MODE ? 'Search keys...' : 'Search keys table...'}
          value={search}
          onChange={onSearchChange}
        />
        {(!!options.create || options.cards) && (
          <div className="grb-main-page-header-buttons">
            {!!options.create && (
              <Button
                variant="contained"
                color="primary"
                aria-label="add"
                onClick={handlePromptCreateKey}
                id="create-api-key"
                disabled={createKeyDialog.creating}
              >
                Create API Key
              </Button>
            )}
            {options.cards && (
              <React.Fragment>
                <div className="grb-vertical-separator" />
                <Tooltip title="Show api keys as tiles or in a table">
                  <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={handleMode}
                    aria-label="display mode"
                  >
                    <ToggleButton
                      value={CARDS_MODE}
                      aria-label="cards mode"
                      id="apikeys-card-mode"
                    >
                      <CardsIcon />
                    </ToggleButton>
                    <ToggleButton
                      value={TABLE_MODE}
                      aria-label="table mode"
                      id="apikeys-table-mode"
                    >
                      <TableIcon />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Tooltip>
              </React.Fragment>
            )}
          </div>
        )}
      </React.Fragment>
    );

    const callbacks = {
      onDeleteKey: handlePromptDeleteKey,
      onEditKey: handlePromptEditKey,
      onDisableKey: handleDisableKey,
      onGenerateKeySecret: handlePromptGenerateSecretKey,
    };

    return (
      <AppMainPage
        {...props}
        variant={FIT_TO_PAGE_VARIANT}
        pageTitle={options.getPageTitle ? options.getPageTitle(rest) : undefined}
        TrailingBar={HeaderBar}
        className="grb-api-keys-page"
        rowCount={apiKeysCount}
        refreshButtonId="update-api-keys"
        onRefresh={onUpdateAPIKeys}
        refreshButtonTooltip="Update API keys"
        backTo={backTo}
        help={configuration && configuration.help}
      >
        <div className={classnames('grb-api-keys-ctnr', 'grb-fullpage', mode !== CARDS_MODE && 'MuiPaper-elevation4')}>
          {(mode === CARDS_MODE) ? (
            <CardsPanel
              {...rest}
              {...callbacks}
              className="grb-api-keys-cards"
              cardObjects={filteredKeys}
              cardComponent={APIKeyCard}
              getFilterValues={getFilterValues}
              onOpenMenu={onOpenMenu}
              rowCount={apiKeysCount}
              getId={getKeyId}
              newlyCreated={selected}
            />
          ) : (
            <TablePanel
              {...rest}
              {...rest.component}
              {...callbacks}
              {...tableProps}
              apiKeys={filteredKeys}
              viewService={extViewService}
              apiKeysCount={apiKeysCount}
              onOpenMenu={onOpenMenu}
              getObjectId={getKeyId}
              noDownload={options.noDownload}
              selected={selected}
              onSelect={onSelect}
              getExtraFilterValues={getExtraFilterValues}
            />
          )}
          {messageDialogOpen && (
            <MessageDialog
              title={dialogMessage.title}
              message={dialogMessage.message}
              onClose={handleCloseMessageDialog}
              open={messageDialogOpen}
            />
          )}
          {confirmDialog.open && <ConfirmDialog {...confirmDialog} maxWidth="md" />}
          {(!!newCreatedAPIKey || !!newGeneratedSecretAPIKey) && !!NewCreatedAPIKeyDialog && (
            <NewCreatedAPIKeyDialog
              apiKey={newCreatedAPIKey || newGeneratedSecretAPIKey}
              newSecretOnly={!!newGeneratedSecretAPIKey}
              apiKeyService={apiKeyService}
              onClose={handleCloseAPIKeyDialog}
              open
            />
          )}
          {createKeyDialog.open && (
            <CreateAPIKeyDialog
              {...rest}
              viewService={viewService}
              title="Select a license for the API Key to create"
              onClose={handleCreateKey}
              {...createDialogProps}
              open
            />
          )}
          {editKeyDialog.open && (
            <EditAPIKeyDialog
              {...rest}
              {...editDialogProps}
              viewService={viewService}
              onClose={handleEditKey}
              apiKey={editKeyDialog.key}
              open
            />
          )}
          <Menu
            {...props}
            {...menuProps}
            {...callbacks}
            id="key-menu"
          />
        </div>
      </AppMainPage>
    );
  };

  APIKeysContainer.propTypes = {
    apiKeys: PropTypes.arrayOf(PropTypes.shape({})),
    viewService: PropTypes.shape({
      onError: PropTypes.func,
      getComponent: PropTypes.func,
    }).isRequired,
    apiKeyService: PropTypes.shape({
      validateCreateApiKey: PropTypes.func,
      createApiKey: PropTypes.func,
      validateDeleteApiKey: PropTypes.func,
      deleteApiKey: PropTypes.func,
      validateGenerateApiKeySecret: PropTypes.func,
      generateApiKeySecret: PropTypes.func,
      getDownloadLicenseURL: PropTypes.func,
      validateDisableKey: PropTypes.func,
      disableApiKey: PropTypes.func,
      validateEditApiKey: PropTypes.func,
      editApiKey: PropTypes.func,
    }).isRequired,
    component: PropTypes.shape({}).isRequired,
    route: PropTypes.shape({
      label: PropTypes.string,
      setContext: PropTypes.func,
      context: PropTypes.shape({
        filter: PropTypes.shape({}),
        filterQuery: PropTypes.string,
      }),
      subscribeContextChange: PropTypes.func,
      unsubscribeContextChange: PropTypes.func,
    }).isRequired,
    filter: PropTypes.shape({
      overview: PropTypes.string,
      mode: PropTypes.string,
    }),
    arrayFilter: PropTypes.func,
    onFilterChange: PropTypes.func,
    filterEditor: PropTypes.shape({}),
    onSnackbarMessage: PropTypes.func.isRequired,
    onUpdateAPIKeys: PropTypes.func,
    search: PropTypes.string,
    onSearchChange: PropTypes.func,
    location: PropTypes.shape({}),
    configuration: PropTypes.shape({
      help: PropTypes.shape({}),
    }),
  };

  APIKeysContainer.defaultProps = {
    apiKeys: undefined,
    filter: undefined,
    arrayFilter: undefined,
    onFilterChange: undefined,
    filterEditor: undefined,
    onUpdateAPIKeys: undefined,
    search: undefined,
    onSearchChange: undefined,
    location: undefined,
    configuration: undefined,
  };

  const entities = [
    options.keysEntity || API_KEYS_ENTITY,
  ].concat(options.entities || []);
  const FilterAPIKeysContainer = withPageFilter(APIKeysContainer, KeyFilter(options));
  const PropMapper = (props) => {
    const { [licensesEntity]: licenses, ...rest } = props;
    return (
      <FilterAPIKeysContainer {...rest} licenses={licenses} />
    );
  };
  return withStoreData(PropMapper, {
    entities,
    onUpdateStore: options.refreshButton ? 'onUpdateAPIKeys' : undefined,
  });
};
